import { Link } from "react-router-dom";
import { ArrowRight, Building2, Car, Sparkles } from "lucide-react";
import { LinkButton } from "@/components/ui/LinkButton";

/**
 * Closing conversion band for the public homepage — the funnel's last push.
 *
 * Renders a full-width, intentionally-dark band (`bg-ink` / ink tokens, so it
 * is flip-safe and reads identically in light and dark themes) with a confident
 * headline, a short supporting line, and DUAL co-equal CTAs that serve both
 * audiences the platform sells to:
 *   - companies → /register?type=business ("Get started")
 *   - drivers   → /register?type=driver   ("Sign up as a driver")
 * plus a quiet tertiary "Talk to us" → /#contact for the not-yet-ready visitor.
 *
 * Purely presentational: no props, no data, no auth — dead code until a homepage
 * integration unit composes it in. Token classes only; the cobalt-tinted texture
 * and mono eyebrow echo the hero's departure-board language for cohesion.
 *
 * @returns The final call-to-action section.
 */
export function FinalCtaSection() {
  return (
    <section
      aria-labelledby="final-cta-heading"
      className="relative isolate overflow-hidden bg-ink text-ink-foreground"
    >
      {/* Atmospheric depth — token-tinted, decorative only. A cobalt glow anchored
          bottom-left and a teal "upgrade" glow top-right give the flat ink band
          dimension without raw colors or shadows on an in-flow surface.
          color-mix lets us tint design-system OKLCH tokens at low alpha (the
          same idiom globals.css uses) without raw palette names or hex. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 opacity-70 [background:radial-gradient(60%_80%_at_15%_115%,color-mix(in_oklab,var(--color-primary-600)_35%,transparent),transparent_60%),radial-gradient(45%_70%_at_92%_-10%,color-mix(in_oklab,var(--color-upgrade-500)_22%,transparent),transparent_55%)]"
      />
      {/* Hairline grid texture — subtle dispatch-console scanlines. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.06] [background-image:linear-gradient(to_right,var(--color-ink-foreground)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-ink-foreground)_1px,transparent_1px)] [background-size:56px_56px]"
      />

      <div className="mx-auto max-w-7xl px-4 py-20 text-center sm:px-6 lg:px-8 lg:py-28">
        {/* Mono eyebrow — echoes the hero departure board's split-flap texture. */}
        <span className="inline-flex items-center gap-1.5 rounded-full border border-ink-foreground/15 bg-ink-foreground/5 px-3 py-1 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-ink-foreground/70">
          <Sparkles
            className="h-3.5 w-3.5"
            strokeWidth={1.75}
            aria-hidden="true"
          />
          Get started today
        </span>

        <h2
          id="final-cta-heading"
          className="mx-auto mt-6 max-w-3xl text-balance text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl"
        >
          Ready to modernize your operation?
        </h2>
        <p className="mx-auto mt-5 max-w-2xl text-pretty text-lg text-ink-foreground/70 sm:text-xl">
          Whether you run a fleet or drive your own car, RideReserv puts
          bookings, dispatch, and customers in one console. Get set up fast and
          go live once you&rsquo;re approved — no contracts.
        </p>

        {/* DUAL co-equal CTAs. Companies get the cobalt primary; drivers get a
            high-contrast light fill so both paths read as equally prominent on
            the dark band. Tertiary "Talk to us" stays a quiet link. */}
        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
          <LinkButton
            to="/register?type=business"
            size="lg"
            className="w-full justify-center focus:ring-offset-ink sm:w-auto"
          >
            <Building2
              className="mr-2 h-4 w-4 shrink-0"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            Get started
            <ArrowRight
              className="ml-2 h-4 w-4 shrink-0"
              strokeWidth={1.75}
              aria-hidden="true"
            />
          </LinkButton>
          {/* Co-equal driver CTA. A CONSTANT light fill keyed to the ink tokens
              (which never flip) — so it stays light-on-dark in BOTH themes and
              reads as an equally-weighted inverse of the cobalt primary, not a
              subordinate outline. Overriding the variant fill here (via
              tailwind-merge in cn) avoids `secondary`'s neutral-100, which would
              go dark-on-dark in the dark theme on this constant ink band. */}
          <LinkButton
            to="/register?type=driver"
            variant="secondary"
            size="lg"
            className="w-full justify-center bg-ink-foreground text-ink hover:bg-ink-foreground/90 focus:ring-ink-foreground focus:ring-offset-ink sm:w-auto"
          >
            <Car
              className="mr-2 h-4 w-4 shrink-0"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            Sign up as a driver
          </LinkButton>
        </div>

        <p className="mt-7 text-sm text-ink-foreground/60">
          Just exploring?{" "}
          <Link
            to="/#contact"
            className="inline-flex min-h-11 items-center rounded-sm font-medium text-primary-400 underline-offset-4 transition-colors pointer-fine:min-h-9 hover:text-primary-300 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-ink"
          >
            Talk to us
            <ArrowRight
              className="ml-1 h-3.5 w-3.5 shrink-0"
              strokeWidth={1.75}
              aria-hidden="true"
            />
          </Link>
        </p>

        {/* Non-numeric reassurance row — no fabricated stats or metrics, and no
            phantom free-trial promise (there is no self-serve trial for
            companies; onboarding is guided). */}
        <p className="mt-8 font-mono text-xs uppercase tracking-[0.18em] text-ink-foreground/60">
          We&rsquo;ll help you get set up · No long-term contracts
        </p>
      </div>
    </section>
  );
}
